import dayjs from "dayjs"
import { t } from "i18next"
import { dateTimeFunction } from "utils/date"
import { Form } from "antd"
import { Checkbox, Group, Icon, Number, Text } from "components/common"
import { numberProps } from "./constants"
import { joinBytItems, localisedValue } from "utils"
import { Separator } from "components/custom"
import theme from "utils/themeVars"

export const fieldMapper = (imageData, paymentObject, form) => {
  if (imageData) {
    form.setFieldsValue(
      Object?.keys(paymentObject)?.reduce((obj, item) => {
        const imageKey = paymentObject[item]
        const raw = imageData[imageKey]
        let value
        if (imageKey === "date") {
          value =
            raw == null || raw === "null" ? "" : dateTimeFunction({ date: raw, Default: true })
        } else if (raw == null || raw === "null") {
          value = ""
        } else if (typeof raw === "object" && raw !== null && !(raw instanceof Date)) {
          value = raw?.key
        } else {
          value = raw
        }
        return { ...obj, [item]: value }
      }, {})
    )
  }
}

export const customValidatorForCDP = (value, project) => {
  if ((+value > 100 || +value < project?.ProjectFinancial?.completeDownPayment) && value !== "") {
    return Promise.reject(
      `${t("CDP committment must be between")}  ${project?.ProjectFinancial?.completeDownPayment}% ${t("and")} 100%`
    )
  }
  return Promise.resolve()
}

export const customValidatorForAddPayment = (
  value,
  saleEvent,
  minTokenDetails,
  leadSalesInfo,
  stateManager,
  isEscrowSelfFinance
) => {
  let regularTokenAmount = Math.round(
    minTokenDetails.minTokenUnit === "amount"
      ? minTokenDetails.minToken
      : minTokenDetails.tokenWithUnit * stateManager?.dealPrice
  )
  let tokenAmount = Math.round(saleEvent ? +saleEvent?.minAmount : regularTokenAmount)
  if (value === "" || value === null) {
    return Promise.resolve()
  }
  if (+value < tokenAmount) {
    return Promise.reject(t("Amount can not be lower than token"))
  }
  if (+value > regularTokenAmount && isEscrowSelfFinance) {
    return Promise.reject(t("Amount cannot be greater than token for billing type escrow"))
  }
  if (+value > leadSalesInfo?.dealPrice) {
    return Promise.reject(t("Amount exceeds deal price"))
  }
  if (+value > stateManager?.dealPrice) {
    return Promise.reject(t("Amount exceeds deal price"))
  }
  return Promise.resolve()
}

export const customValidatorForVASAddPayment = (value, leadSalesInfo, stateManager, vasEnabled, vasFees) => {
  if (value === "" || value === null) {
    return Promise.resolve()
  }
  if (vasEnabled && +value > Math.round(vasFees)) {
    return Promise.reject(t("Amount cannot be greater than VAS Fee"))
  }
  if (vasEnabled && +value < Math.round(vasFees)) {
    return Promise.reject(t("Amount can not be lower than VAS Fee"))
  }
  if (+value > leadSalesInfo?.dealPrice) {
    return Promise.reject(t("Amount exceeds deal price"))
  }
  if (+value > stateManager?.dealPrice) {
    return Promise.reject(t("Amount exceeds deal price"))
  }
  return Promise.resolve()
}

export const disableTimeRange = (date, timerange, range) => {
  if (date) {
    const currentMoment = dayjs()
    if (date.isSame(currentMoment, "day")) {
      return {
        disabledHours: () => range(0, currentMoment.hour()),
        ...(date.hour() === currentMoment.hour() && { disabledMinutes: () => range(0, currentMoment.minute()) }),
      }
    }
    if (date.isSame(timerange?.endDate, "day")) {
      return {
        disabledHours: () => range(timerange?.endDate?.hour() + 1, 24),
        ...(date.hour() === timerange?.endDate?.hour() && {
          disabledMinutes: () => range(timerange?.endDate?.minute() + 1, 60),
          ...(date.minute() === timerange?.endDate?.minute() && {
            disabledSeconds: () => range(timerange?.endDate?.second(), 60),
          }),
        }),
      }
    }
    return {
      disabledHours: () => range(0, 0),
      disabledMinutes: () => range(0, 0),
    }
  }
  return {}
}

const getCurrentTokenData = (tokenDetails, dealPricenum) => {
  return tokenDetails.minTokenUnit === "amount" ? tokenDetails.tokenWithUnit : dealPricenum * tokenDetails.tokenWithUnit
}

export const setProgressionBar = (amount, project, dealPrice, minTokenDetails, cdpCommitment) => {
  const dealPricenum = +dealPrice
  const amountNum = +amount
  if (!!dealPricenum) {
    if (amountNum < getCurrentTokenData(minTokenDetails, dealPricenum)) {
      return 0
    }
    if (
      amountNum >= getCurrentTokenData(minTokenDetails, dealPricenum) &&
      amountNum < dealPricenum * project?.pdpPercentage
    ) {
      return 1
    }
    if (
      amountNum >= dealPricenum * project?.pdpPercentage &&
      amountNum < dealPricenum * ((cdpCommitment ?? project?.ProjectFinancial?.completeDownPayment) / 100)
    ) {
      return 2
    }
    if (amountNum >= dealPricenum * (cdpCommitment ?? project?.ProjectFinancial?.completeDownPayment / 100)) {
      return 3
    }
  }
  return 0
}

export const checkAbovePDP = (leadSalesInfo, amount, stateManager, project) => {
  if (leadSalesInfo?.current >= 0 && +amount > 0) {
    return +amount >= leadSalesInfo?.remainParDowPayment ? true : false
  } else if (amount > 0) {
    return +amount >= stateManager?.dealPrice * project?.pdpPercentage ? true : false
  }
  return false
}

export const shouldRenderFormItem = (type, project, stateManager, leadSalesInfo) => {
  const hasPaymentProof =
    stateManager?.project?.paymentProof || project?.paymentProof || leadSalesInfo?.project?.paymentProof
  return !!((hasPaymentProof && type?.imageData) || (!hasPaymentProof && typeof type === "object"))
}

export const customValidatorForSaleType = (value, mortgageAssessment) => {
  if (value === "mortgage" && !mortgageAssessment) {
    return Promise.reject(t("Please complete the mortgage assessment to add a Mortgage Sale."))
  } else if (value === "mortgage" && mortgageAssessment?.snapshot?.netLoanCoverage < 70) {
    return Promise.reject(t("Can not add a mortgage sale because the buyer is ineligible for mortgage"))
  }
  return Promise.resolve()
}

export const flag = leadSalesInfo => {
  return !!(
    leadSalesInfo?.isReservedWithReservedStatus ||
    leadSalesInfo?.contracts[0]?.ReservationQueueAgainstListing?.isReserved
  )
}

export default function FieldObserver({ form, names = [], shouldUpdate, children }) {
  return (
    <Form.Item
      noStyle
      shouldUpdate={
        shouldUpdate ||
        ((prev, curr) =>
          names.some(n => {
            const prevVal = form.getFieldValue(n, prev)
            const currVal = form.getFieldValue(n, curr)
            return prevVal !== currVal
          }))
      }
    >
      {({ getFieldValue }) => {
        const values = names.reduce((acc, n) => {
          acc[n] = getFieldValue(n)
          return acc
        }, {})
        return children?.(values)
      }}
    </Form.Item>
  )
}

export const customVASComponent = (stateManager, leadSalesInfo, VASOptionalSelection) => data => {
  let total = 0
  const dealPrice = stateManager?.dealPrice || 0

  return (
    <Group vertical gap="6px" direction="column">
      &nbsp;
      {data?.map((fee, index) => {
        const { amountBy, amountValue = 0, taxPercentage = 0 } = fee || {}
        const isAmountType = amountBy === "amount"
        const baseValue = isAmountType ? amountValue : (dealPrice * amountValue) / 100
        const tax = (baseValue * taxPercentage) / 100
        const value = baseValue + tax
        if (!fee?.isMandatory && !!VASOptionalSelection?.[fee?.key]) {
          total += value
        } else if (fee?.isMandatory) {
          total += value
        }

        return (
          <div key={index}>
            <Number {...numberProps} isCurrency tooltip value={value} />
          </div>
        )
      })}
      <span className="semiBold">
        <Number {...numberProps} isCurrency tooltip value={total} className="semiBold" />
      </span>
      <span className="semiBold">
        <Number
          {...numberProps}
          isCurrency
          tooltip
          value={leadSalesInfo?.isVasRemaining ? leadSalesInfo?.vasRemianingAmount : total}
          className="semiBold"
        />
      </span>
    </Group>
  )
}

export const vasBreakDownKeys = () => ({
  buyer_fee: t("Buyer Fee"),
  mortgage_fee: t("Mortgage Fee"),
})

export const ProjectInfoDisplay = ({ projectInfo }) => (
  <div>
    {projectInfo.map((e, i) => (
      <Text key={i} type={i === 0 && "info"} className="semiBold" style={{ gap: "initial" }}>
        {e}
      </Text>
    ))}
  </div>
)

export const DefaultProjectDisplay = ({ project, unit }) => (
  <div>
    {joinBytItems(
      [
        project?.name && (
          <Text key="projectName" type="info" className="semiBold">
            {localisedValue({ object: project, key: "name" })}
          </Text>
        ),
        <Text className="semiBold">
          {`${localisedValue({ object: unit, key: "name" })} ${unit?.Type ? `(${localisedValue({ object: unit?.Type, key: "name" })})` : ""
            }`}
        </Text>,
      ]?.filter(Boolean),
      <Separator />
    )}
  </div>
)

export const VASBreakdownLabel = ({ vasFees, setOptionalSelections }) => (
  <Group vertical gap="6px">
    <Text className="semiBold">
      {t("BSF Services Breakdown")}{" "}
      <Icon
        icon="BsInfo"
        tooltipText={t("BSF fees are collected upfront and required to book this unit")}
        size="1.2em"
        color={theme["gray900"]}
        style={{
          backgroundColor: theme["gray400"],
          borderRadius: "1rem",
          "--icon-color": "#707070",
        }}
      />
    </Text>
    {vasFees?.map((fee, index) => {
      const isMandatory = fee?.isMandatory
      return (
        <Checkbox
          key={index}
          {...(fee?.isMandatory && { checked: true, disabled: true })}
          style={{ marginLeft: 12 }}
          onChange={e => {
            if (isMandatory) return
            setOptionalSelections(prev => ({
              ...prev,
              [fee?.key]: e?.target?.checked,
            }))
          }}
        >
          {vasBreakDownKeys()?.[fee?.key]}
        </Checkbox>
      )
    })}
    <Text className="bold">{t("BSF Fee Total")}</Text>
    <Text className="semiBold">{t("BSF Due Amount")}</Text>
  </Group>
)
